import React, { useState, useEffect, useMemo } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane, Row, Col } from 'reactstrap';
import classnames from 'classnames';
import ModalComponent from '../../common/Modal/modal';
import { CustomButton, showToaster, toasterTypes } from '../../../widgets';
import { FieldItem, FIELD_TYPES } from '../../../widgets/fields';
import useFieldItem from '../../../widgets/fields/UseFieldItem';
import Table from '../../../widgets/table';
import { getActiveStyle, generateBibliography, getCachedStyleXML } from '../services/CitationService/CitationStyleService';
import './ReferenceManager.scss';

const referenceTypes = [
  { label: 'Journal Article', value: 'article' },
  { label: 'Book', value: 'book' },
  { label: 'Conference Paper', value: 'conference' },
  { label: 'Website', value: 'website' },
  { label: 'Thesis', value: 'thesis' },
  { label: 'Other', value: 'other' },
];

const FIELDS = {
  type: {
    name: 'type',
    label: 'Reference Type',
    placeholder: 'Select reference type',
    type: FIELD_TYPES.DROP_DOWN,
    values: referenceTypes,
    isMandatory: true
  },
  authors: {
    name: 'authors',
    label: 'Authors',
    placeholder: 'e.g., Smith, J., & Doe, A.',
    type: FIELD_TYPES.TEXT,
    isMandatory: true
  },
  year: {
    name: 'year',
    label: 'Year',
    placeholder: 'e.g., 2024',
    type: FIELD_TYPES.TEXT,
    isMandatory: true
  },
  title: {
    name: 'title',
    label: 'Title',
    placeholder: 'Title of the work',
    type: FIELD_TYPES.TEXT,
    isMandatory: true
  },
  journal: {
    name: 'journal',
    label: 'Journal/Conference Name',
    placeholder: 'Journal or conference name',
    type: FIELD_TYPES.TEXT
  },
  volume: {
    name: 'volume',
    label: 'Volume',
    placeholder: 'Volume number',
    type: FIELD_TYPES.TEXT
  },
  pages: {
    name: 'pages',
    label: 'Pages',
    placeholder: 'e.g., 123-145',
    type: FIELD_TYPES.TEXT
  },
  publisher: {
    name: 'publisher',
    label: 'Publisher',
    placeholder: 'Publisher name',
    type: FIELD_TYPES.TEXT
  },
  url: {
    name: 'url',
    label: 'URL',
    placeholder: 'https://example.com',
    type: FIELD_TYPES.TEXT
  },
  doi: {
    name: 'doi',
    label: 'DOI (Optional)',
    placeholder: 'Digital Object Identifier',
    type: FIELD_TYPES.TEXT
  },
  notes: {
    name: 'notes',
    label: 'Notes (Optional)',
    placeholder: 'Additional notes',
    type: FIELD_TYPES.TEXT_AREA
  }
};

const initialValues = {
  type: referenceTypes[0],
  authors: '',
  year: '',
  title: '',
  journal: '',
  volume: '',
  pages: '',
  publisher: '',
  url: '',
  doi: '',
  notes: '',
};

const ReferenceManager = ({ visible, onClose, documentId }) => {
  const [references, setReferences] = useState([]);
  const [editingReference, setEditingReference] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activeStyle, setActiveStyle] = useState(getActiveStyle());

  const [values, fields, handleChange, { validateValues, reset, updateValue }] = useFieldItem(FIELDS, initialValues, {});

  useEffect(() => {
    const handleStyleChange = (e) => {
      setActiveStyle(e.detail || getActiveStyle());
    };
    window.addEventListener('citationStyleChanged', handleStyleChange);
    return () => window.removeEventListener('citationStyleChanged', handleStyleChange);
  }, []);

  const styleXML = getCachedStyleXML(activeStyle.id);
  const cslResults = useMemo(() => generateBibliography(references, styleXML), [references, styleXML]);

  useEffect(() => {
    if (visible && documentId) {
      loadReferences();
    }
  }, [visible, documentId]);

  const loadReferences = () => {
    const stored = localStorage.getItem(`${documentId}_references`);
    if (stored) {
      setReferences(JSON.parse(stored));
    }
  };

  const saveReferences = (refs) => {
    const numberedRefs = refs.map((ref, index) => ({
      ...ref,
      number: index + 1,
    }));
    localStorage.setItem(`${documentId}_references`, JSON.stringify(numberedRefs));
    setReferences(numberedRefs);
    localStorage.setItem('isChanged', 'true');
    window.dispatchEvent(new Event('referencesUpdated'));
  };

  const handleAddReference = (formValues) => {
    const newReference = {
      id: Date.now().toString(),
      authors: formValues.authors,
      year: formValues.year,
      title: formValues.title,
      type: formValues.type?.value || 'other',
      journal: formValues.journal,
      volume: formValues.volume,
      pages: formValues.pages,
      publisher: formValues.publisher,
      url: formValues.url,
      doi: formValues.doi,
      notes: formValues.notes,
      isCited: false,
      createdAt: new Date().toISOString(),
    };

    const updatedRefs = [...references, newReference];
    saveReferences(updatedRefs);
    reset();
    setActiveTab('list');
  };

  const handleUpdateReference = (formValues) => {
    const updatedRefs = references.map((ref) =>
      ref.id === editingReference.id ? { 
        ...ref, 
        authors: formValues.authors,
        year: formValues.year,
        title: formValues.title,
        type: formValues.type?.value || 'other',
        journal: formValues.journal,
        volume: formValues.volume,
        pages: formValues.pages,
        publisher: formValues.publisher,
        url: formValues.url,
        doi: formValues.doi,
        notes: formValues.notes
      } : ref
    );
    saveReferences(updatedRefs);
    setEditingReference(null);
    reset();
    setActiveTab('list');
  };

  const handleDeleteReference = (id) => {
    const updatedRefs = references.filter((ref) => ref.id !== id);
    saveReferences(updatedRefs);
    setShowDeleteConfirm(false);
    setDeletingId(null);
  };

  const handleEdit = (record) => {
    setEditingReference(record);
    const typeObj = referenceTypes.find(t => t.value === record.type) || referenceTypes[0];
    updateValue({
      ...record,
      type: typeObj
    });
    setActiveTab('add');
  };

  const confirmDelete = (id) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const formatReference = (ref) => {
    const { type, authors, title, year, journal, volume, pages, publisher, url } = ref;
    
    switch (type) {
      case 'article':
        return `${authors} (${year}). ${title}. ${journal}, ${volume}${pages ? `, ${pages}` : ''}.`;
      case 'book':
        return `${authors} (${year}). ${title}. ${publisher}.`;
      case 'website':
        return `${authors} (${year}). ${title}. Retrieved from ${url}`;
      case 'conference':
        return `${authors} (${year}). ${title}. In ${journal}${pages ? ` (pp. ${pages})` : ''}.`;
      default:
        return `${authors} (${year}). ${title}.`;
    }
  };

  const columns = useMemo(() => [
    {
      label: '#',
      accessor: 'number',
      width: 60,
      Cell: ({ value }) => `[${value}]`,
    },
    {
      label: 'Type',
      accessor: 'type',
      width: 100,
      Cell: ({ value }) => value?.charAt(0).toUpperCase() + value?.slice(1),
    },
    {
      label: 'Reference',
      accessor: 'formatted',
      Cell: ({ row }) => {
        const styled = cslResults?.entries?.[row.index];
        if (styled) {
          return <span 
            className="reference-text" 
            dangerouslySetInnerHTML={{ __html: styled }} 
            style={{ fontSize: '13px', display: 'block', lineHeight: '1.4' }}
          />;
        }
        return formatReference(row.original);
      },
    },
    {
      label: 'Actions',
      accessor: 'actions',
      width: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <i 
            className="icon icon-edit cursor-pointer" 
            onClick={() => handleEdit(row.original)}
            title="Edit"
            style={{ fontSize: '18px', color: 'var(--primary-color, #006BD9)' }}
          />
          <i 
            className="icon icon-delete cursor-pointer text-danger" 
            onClick={() => confirmDelete(row.original.id)}
            title="Delete"
            style={{ fontSize: '18px' }}
          />
        </div>
      ),
    },
  ], [references]);

  const handleSubmit = () => {
    if (validateValues(['type', 'authors', 'year', 'title'])) {
      showToaster(toasterTypes.ERROR, "Please enter all mandatory fields");
      return;
    }

    if (editingReference) {
      handleUpdateReference(values);
    } else {
      handleAddReference(values);
    }
  };

  const handleCancel = () => {
    reset();
    setEditingReference(null);
    setActiveTab('list');
  };

  return (
    <>
      <ModalComponent
        header="Reference Manager"
        isOpen={visible}
        onClose={onClose}
        width="900px"
        footer={<></>}
        customClass="reference-manager-modal"
        body={
          <div className="reference-manager-content">
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'list' })}
                  onClick={() => setActiveTab('list')}
                  style={{ cursor: 'pointer' }}
                >
                  References List
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'add' })}
                  onClick={() => setActiveTab('add')}
                  style={{ cursor: 'pointer' }}
                >
                  {editingReference ? "Edit Reference" : "Add Reference"}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="list">
                <div className="references-list">
                  <div className="mb-3">
                    <CustomButton
                      type="primary"
                      text="Add New Reference"
                      onClick={() => setActiveTab('add')}
                    />
                  </div>
                  <Table
                    tableProps={columns}
                    data={references}
                    isSearch={true}
                    customClassName="reference-table"
                  />
                </div>
              </TabPane>

              <TabPane tabId="add">
                <div className="reference-form p-2">
                  <Row>
                    <Col md={12}>
                      <FieldItem
                        {...FIELDS.type}
                        value={values.type}
                        onChange={(val) => handleChange('type', val)}
                        touched={fields.type?.hasError}
                        error={fields.type?.errorMsg}
                      />
                    </Col>
                    
                    <Col md={12}>
                      <FieldItem
                        {...FIELDS.authors}
                        value={values.authors}
                        onChange={(val) => handleChange('authors', val)}
                        touched={fields.authors?.hasError}
                        error={fields.authors?.errorMsg}
                      />
                    </Col>

                    <Col md={4}>
                      <FieldItem
                        {...FIELDS.year}
                        value={values.year}
                        onChange={(val) => handleChange('year', val)}
                        touched={fields.year?.hasError}
                        error={fields.year?.errorMsg}
                      />
                    </Col>

                    <Col md={8}>
                      <FieldItem
                        {...FIELDS.title}
                        value={values.title}
                        onChange={(val) => handleChange('title', val)}
                        touched={fields.title?.hasError}
                        error={fields.title?.errorMsg}
                      />
                    </Col>

                    {(values.type?.value === 'article' || values.type?.value === 'conference') && (
                      <Col md={12}>
                        <FieldItem
                          {...FIELDS.journal}
                          value={values.journal}
                          onChange={(val) => handleChange('journal', val)}
                        />
                      </Col>
                    )}

                    {values.type?.value === 'article' && (
                      <Col md={6}>
                        <FieldItem
                          {...FIELDS.volume}
                          value={values.volume}
                          onChange={(val) => handleChange('volume', val)}
                        />
                      </Col>
                    )}

                    {(values.type?.value === 'article' || values.type?.value === 'conference') && (
                      <Col md={6}>
                        <FieldItem
                          {...FIELDS.pages}
                          value={values.pages}
                          onChange={(val) => handleChange('pages', val)}
                        />
                      </Col>
                    )}

                    {values.type?.value === 'book' && (
                      <Col md={12}>
                        <FieldItem
                          {...FIELDS.publisher}
                          value={values.publisher}
                          onChange={(val) => handleChange('publisher', val)}
                        />
                      </Col>
                    )}

                    {values.type?.value === 'website' && (
                      <Col md={12}>
                        <FieldItem
                          {...FIELDS.url}
                          value={values.url}
                          onChange={(val) => handleChange('url', val)}
                        />
                      </Col>
                    )}

                    <Col md={12}>
                      <FieldItem
                        {...FIELDS.doi}
                        value={values.doi}
                        onChange={(val) => handleChange('doi', val)}
                      />
                    </Col>

                    <Col md={12}>
                      <FieldItem
                        {...FIELDS.notes}
                        value={values.notes}
                        onChange={(val) => handleChange('notes', val)}
                        rows={3}
                      />
                    </Col>

                    <Col md={12} className="mt-4">
                      <div className="d-flex gap-2">
                        <CustomButton 
                          type="primary" 
                          text={editingReference ? 'Update Reference' : 'Add Reference'}
                          onClick={handleSubmit}
                        />
                        <CustomButton 
                          type="secondary"
                          text="Cancel"
                          onClick={handleCancel}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </TabPane>
            </TabContent>
          </div>
        }
      />

      <ModalComponent
        header="Confirm Delete"
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        width="400px"
        body={
          <div className="p-3">
            Are you sure you want to delete this reference?
          </div>
        }
        footer={
          <div className="d-flex justify-content-end gap-2 p-2">
            <CustomButton 
              type="secondary" 
              text="No" 
              onClick={() => setShowDeleteConfirm(false)} 
            />
            <CustomButton 
              type="danger" 
              text="Yes, Delete" 
              onClick={() => handleDeleteReference(deletingId)} 
            />
          </div>
        }
      />
    </>
  );
};

export default ReferenceManager;
